<template>
	<div>
		<v-container
			grid-list-md
			my-2
		>
			<v-layout
				row
				wrap
				justify-center
			>
				<v-flex
					xs12
					md8
					pa-0
				>
					<slot name="alert"/>
				</v-flex>
				<v-flex
					xs12
					md8
					class="text-xs-center"
				>
					<v-btn
						color="success"
						@click="archiveDialog = true"
					>
						View Archived
					</v-btn>
					<NewCategory
						v-if="$userCan('manage_categories') && !project.isArchived"
						:existing-categories="categoryNames"
						:archived-categories="archivedNames"
					/>
					<div
						v-if="categoryTree.length < 1"
						class="title text-xs-center"
					>
						No categories
					</div>
					<DraggableTree
						v-if="categoryTree.length > 0 && !project.isArchived"
						ref="draggable"
						:data="categoryTree"
						:indent="40"
						:draggable="$userCan('manage_categories')"
						@change="submit"
					>
						<div slot-scope="{data, store}">
							<v-layout
								v-if="!data.isArchived"
								row
								align-center
							>
								<v-btn
									v-if="data.children && data.children.length"
									flat
									icon
									color="error"
									@click="store.toggleOpen(data)"
								>
									<v-icon>{{ data.open ? 'remove' : 'add' }}</v-icon>
								</v-btn>
								<span
									v-else-if="data.level === 1"
									class="cat-padding"
								/>
								<v-icon
									v-else
									color="primary"
									class="cat-padding"
								>
									subdirectory_arrow_right
								</v-icon>
								<v-flex xs12>
									<v-card
										v-if="!data.isDragPlaceHolder"
										hover
										class="pa-2"
									>
										<v-layout
											row
											align-center
											justify-space-between
										>
											<div class="subheading pl-3 truncate">
												{{ data.name }}
											</div>
											<div class="d-flex">
												<v-btn
													v-if="$userCan('manage_categories') && !project.isArchived"
													icon
													flat
													@click="setAndShowEditCategory(data)"
												>
													<v-icon>edit</v-icon>
												</v-btn>
												<v-btn
													v-if="$userCan('manage_categories') && !project.isArchived"
													:disabled="data.children.length > 0"
													icon
													flat
													color="error"
													@click="deleteCategory(data)"
												>
													<v-icon>delete</v-icon>
												</v-btn>
											</div>
										</v-layout>
									</v-card>
								</v-flex>
							</v-layout>
						</div>
					</DraggableTree>
				</v-flex>
			</v-layout>
		</v-container>
		<v-dialog
			ref="dialog"
			v-model="editCategory.show"
			max-width="800px"
		>
			<v-card v-if="editCategory.show">
				<v-container grid-list-xl>
					<CategoryForm
						:edit-category="editCategory.category"
						:existing-categories="categoryNames"
						:archived-categories="archivedNames"
						@update="updateCategory"
					>
						<v-btn
							slot="buttons"
							flat
							color="error"
							@click="editCategory.show = false"
						>
							close
						</v-btn>
					</CategoryForm>
				</v-container>
			</v-card>
		</v-dialog>
		<v-dialog
			ref="archive"
			v-model="archiveDialog"
			max-width="800px"
			persistent
		>
			<v-card>
				<v-container pb-0>
					<v-layout row>
						<v-flex class="text-xs-center">
							<h1
								v-if="archivedCategories.length > 0"
								class="headline"
							>
								Archived Categories
							</h1>
							<h1
								v-else
								class="title"
							>
								No Archived Categories
							</h1>
						</v-flex>
					</v-layout>
					<v-layout
						row
						wrap
					>
						<v-flex
							v-for="item in archivedCategories"
							:key="item.id"
							xs12
							sm6
							pa-2
						>
							<v-card
								class="pa-2"
							>
								<v-layout
									row
									align-center
									justify-space-between
								>
									<div class="subheading pl-3">
										<span class="truncate">{{ item.name }}</span>
									</div>
									<div>
										<v-btn
											v-if="$userCan('manage_categories') && !project.isArchived"
											flat
											color="success"
											@click="unarchiveCategory(item)"
										>
											Restore
										</v-btn>
									</div>
								</v-layout>
							</v-card>
						</v-flex>
						<v-flex
							xs12
							class="text-xs-center"
						>
							<v-btn
								flat
								color="error"
								class="ma-3"
								@click="archiveDialog = false"
							>
								Close
							</v-btn>
						</v-flex>
					</v-layout>
				</v-container>
			</v-card>
		</v-dialog>
		<Confirm ref="confirm" />
	</div>
</template>

<script>
import CategoryForm from '@/components/Report/CategoryForm';
import NewCategory from '@/components/Report/NewCategory';
import Confirm from '@/components/Confirm';
import { DraggableTree } from 'vue-draggable-nested-tree';
import { Category, Categories } from '@/models/Category';
import rec from '@/util/flatten';

export default {
	components: {
		CategoryForm,
		NewCategory,
		Confirm,
		DraggableTree,
	},
	data() {
		return {
			editCategory: { category: {}, show: false },
			categoryTree: [],
			archiveDialog: false,
		};
	},
	computed: {
		categories() {
			return this.$store.get('categories');
		},
		project() {
			return this.$store.get('projects/currentProject');
		},
		categoryNames() {
			return this.categories.map(o => o.name);
		},
		archivedCategories() {
			return this.$store.get('categoriesArchived').sort((a, b) => {
				if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
				if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
				return 0;
			});
		},
		archivedNames() {
			return this.archivedCategories.map(o => o.name);
		},
	},
	watch: {
		categories: {
			handler(val) {
				if (val.length > 0) {
					this.categoryTree = this.getCategoriesTree(this.$store.copy('categories'));
				}
				else {
					this.categoryTree = [];
				}
			},
			immediate: true,
		},
	},
	methods: {
		setAndShowEditCategory(category) {
			this.editCategory = { category, show: true };
		},
		updateCategory(cat) {
			this.editCategory.show = false;
			this.editCategory.category.name = cat.name;
		},
		deleteCategory(cat) {
			this.$refs.confirm.createConfirm(`This will archive "${cat.name}".`, 'error').then((answer) => {
				if (!answer) {
					return;
				}
				const category = new Category({ id: cat.id, name: cat.name });
				category.delete().then(() => {
					const categories = this.$store.get('categories');
					const index = categories.map(o => o.id).indexOf(cat.id);
					categories.splice(index, 1);
					this.$store.get('categoriesArchived').push({
						id: cat.id,
						name: cat.name,
						level: cat.level,
						isArchived: true,
					});
					this.$notify('success', `Category ${cat.name} has been archived.`);
				}).catch(() => {
					this.$notify('error', `Failed to archive category ${cat.name}.`);
				});
			});
		},
		unarchiveCategory(cat) {
			const category = new Category();
			category.id = cat.id;
			category.name = cat.name;
			category.isArchived = false;
			category.save().then(() => {
				const categories = this.$store.get('categories');
				categories.unshift({
					id: cat.id,
					name: cat.name,
					level: 1,
					isArchived: false,
				});
				const index = this.archivedCategories.map(o => o.id).indexOf(cat.id);
				this.archivedCategories.splice(index, 1);
				this.$notify('success', `Category ${cat.name} has been unarchived.`);
			}).catch(() => {
				this.$notify('error', `Failed to unarchive ${cat.name}.`);
			});
		},
		submit() {
			const cats = this.categoryTree;
			// Next tick required to update DOM data before processing
			this.$nextTick(() => {
				const flattened = this.getFlattenedCategories(cats);
				const categories = new Categories();
				categories.projectId = this.project.id;
				categories.order = flattened;
				categories.save().then(() => {
					this.$store.set('categories', flattened);
				}).catch(() => {
					this.$notify('error', 'Failed to update categories order.');
				});
			});
		},
		// Obtained from: https://stackoverflow.com/questions/44517560/generate-tree-from-flat-array-javascript
		getCategoriesTree(arr) {
			try {
				const levels = [{}];
				arr.forEach((a) => {
					levels.length = a.level;
					levels[a.level - 1].children = levels[a.level - 1].children || [];
					levels[a.level - 1].children.push(a);
					levels[a.level] = a;
				});
				return levels[0].children;
			}
			catch (e) {
				if (process.env.NODE_ENV !== 'development') {
					this.$bugsnag.notify(e, {
						beforeSend: (report) => {
							report.errorMessage = `Category Tree failed to build: ${report.errorMessage}`;
						},
					});
				}
				this.$notify('error', 'Categories failed to build. If this persists, please send an error report.');
				return [];
			}
		},
		getFlattenedCategories(cats) {
			return rec(cats, 0);
		},
	},
};
</script>

<style lang="scss">
.he-tree{
  padding: 20px;
}
.tree-node-inner{
  cursor: pointer;
}
.draggable-placeholder-inner{
  box-sizing: border-box;
  background: rgba(3, 168, 205, 0.5);
  color: #03A8CD;
  text-align: center;
  padding: 0;
  display: flex;
  align-items: center;
	border-radius: .25rem;
	margin-left: 52px;
}
.cat-padding {
	min-width: 52px;
	max-width: 52px;
}
.tree-node-inner-back {
	padding-right: 52px;
}
.truncate {
	overflow: hidden;
	text-overflow: ellipsis;
}
</style>
